import React, { useEffect, useMemo, useState } from 'react'
import {
  Container,
  CssBaseline,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material'
import clinicService from '../../services/clinic.service'
import {
  defaultClinicTheme,
  formatClinicAddress,
  hexToRgba,
  mergeClinicThemeResponse,
  remToPx,
  resolveThemeColor,
} from './clinicThemeDefaults'
import './clinicThemeShell.css'

const socialIconClass = (type = '') => {
  switch (String(type).toUpperCase()) {
    case 'FACEBOOK':
      return 'bi bi-facebook'
    case 'INSTAGRAM':
      return 'bi bi-instagram'
    case 'TWITTER':
    case 'X':
      return 'bi bi-twitter-x'
    case 'LINKEDIN':
      return 'bi bi-linkedin'
    case 'YOUTUBE':
      return 'bi bi-youtube'
    case 'GOOGLE':
    case 'GOOGLE_PAGE':
      return 'bi bi-google'
    case 'YELP':
      return 'bi bi-yelp'
    case 'WEBSITE':
      return 'bi bi-globe2'
    default:
      return 'bi bi-link-45deg'
  }
}

const resolveHref = (value) => {
  if (!value || value === '#') return null
  return String(value).startsWith('http') ? value : `https://${value}`
}

const ClinicThemeShell = ({
  clinicId,
  children,
  contentMaxWidth = 'lg',
  contentClassName = '',
  atmosphere = false,
}) => {
  const [theme, setTheme] = useState(defaultClinicTheme)
  const [clinicContent, setClinicContent] = useState(null)
  const [loading, setLoading] = useState(Boolean(clinicId))

  useEffect(() => {
    if (!clinicId) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    clinicService
      .getClinicTheme(clinicId)
      .then((res) => {
        if (cancelled) return
        const merged = mergeClinicThemeResponse(res.data)
        setTheme(merged.theme)
        setClinicContent(merged.clinicContent)
      })
      .catch(() => {
        if (cancelled) return
        setTheme(defaultClinicTheme)
        setClinicContent(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [clinicId])

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: theme.theme.mode === 'auto' ? 'light' : theme.theme.mode,
          primary: {
            main: theme.theme.colors.primary,
            light: theme.theme.colors.primaryLight,
            dark: theme.theme.colors.primaryDark,
          },
          secondary: {
            main: theme.theme.colors.secondary,
            light: theme.theme.colors.secondaryLight,
          },
          success: { main: theme.theme.colors.success },
          info: { main: theme.theme.colors.info },
          warning: { main: theme.theme.colors.warning },
          error: { main: theme.theme.colors.danger },
          background: {
            default: theme.theme.colors.background,
            paper: theme.theme.colors.surface,
          },
          text: {
            primary: theme.theme.colors.text,
            secondary: theme.theme.colors.textMuted,
          },
        },
        typography: {
          fontFamily: theme.theme.fonts.primary,
          fontSize: remToPx(theme.theme.fonts.size.base),
          h1: { fontSize: remToPx(theme.theme.fonts.size.xl) * 1.5 },
          h2: { fontSize: remToPx(theme.theme.fonts.size.xl) },
          h3: { fontSize: remToPx(theme.theme.fonts.size.large) },
          h4: { fontSize: remToPx(theme.theme.fonts.size.large) * 0.85 },
          h5: { fontSize: remToPx(theme.theme.fonts.size.base) * 1.25 },
          h6: { fontSize: remToPx(theme.theme.fonts.size.base) },
          body1: { fontSize: remToPx(theme.theme.fonts.size.base) },
          body2: { fontSize: remToPx(theme.theme.fonts.size.small) },
        },
        shape: {
          borderRadius: parseInt(theme.theme.borderRadius, 10) || 8,
        },
      }),
    [theme]
  )

  useEffect(() => {
    if (!theme.theme.colors) return
    const root = document.documentElement
    const c = theme.theme.colors
    root.style.setProperty('--primary-color', c.primary)
    root.style.setProperty('--primary-light', c.primaryLight)
    root.style.setProperty('--primary-dark', c.primaryDark)
    root.style.setProperty('--secondary-color', c.secondary)
    root.style.setProperty('--background-color', c.background)
    root.style.setProperty('--surface-color', c.surface)
    root.style.setProperty('--text-color', c.text)
    root.style.setProperty('--text-muted', c.textMuted)
    root.style.setProperty('--border-color', c.border)
    root.style.setProperty('--border-radius', theme.theme.borderRadius)
    root.style.setProperty('--rep-accent', c.primary)
    root.style.setProperty('--rep-accent-soft', hexToRgba(c.primary, 0.12))
    root.style.setProperty('--rep-ink', c.text)
    root.style.setProperty('--rep-muted', c.textMuted)
    root.style.setProperty('--rep-line', c.border)
    root.style.setProperty('--rep-surface', c.surface)
    root.style.setProperty('--rep-radius', theme.theme.borderRadius)
    root.style.setProperty('--rep-good', c.success)
    root.style.setProperty('--rep-good-soft', hexToRgba(c.success, 0.12))
    root.style.setProperty('--rep-bad', c.danger)
    root.style.setProperty('--rep-bad-soft', hexToRgba(c.danger, 0.1))
    root.style.setProperty('--rep-font-body', theme.theme.fonts.primary)
    root.style.setProperty('--rep-font-display', theme.theme.fonts.primary)
  }, [theme])

  if (loading) {
    return (
      <div className="clinic-theme-loading">
        <div className="clinic-theme-spinner" role="status" aria-label="Loading" />
      </div>
    )
  }

  const headerBgColor = resolveThemeColor(
    theme.header.backgroundColor,
    theme.theme.colors.primary
  )
  const footerBgColor = resolveThemeColor(
    theme.footer.backgroundColor,
    theme.theme.colors.primary
  )
  const clinicName = clinicContent?.name || 'Clinic'
  const formattedAddress = formatClinicAddress(clinicContent?.address)
  const phone = clinicContent?.contact?.phone
  const email = clinicContent?.contact?.email
  const website = clinicContent?.contact?.website
  const showLogo =
    theme.header.showLogo &&
    theme.logo.url &&
    theme.logo.url !== '<<CLINIC_LOGO>>'

  const websiteHref = website
    ? website.startsWith('http')
      ? website
      : `https://${website}`
    : null

  // Header text uses the clinic theme's font sizes: base for details, large for the name.
  const baseFontSize = theme.theme?.fonts?.size?.base || '1rem'
  const largeFontSize = theme.theme?.fonts?.size?.large || '1.25rem'

  const pageStyle = {
    fontFamily: theme.theme.fonts.primary,
    backgroundColor: theme.theme.colors.background,
    color: theme.theme.colors.text,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    '--clinic-header-bg': headerBgColor,
    '--clinic-footer-bg': footerBgColor,
    '--clinic-chrome-text': theme.header.textColor,
    '--clinic-footer-text': theme.footer.textColor,
    '--clinic-brand-surface': theme.theme.colors.surface || '#ffffff',
    '--clinic-brand-ink': theme.theme.colors.text,
    '--clinic-brand-muted': theme.theme.colors.textMuted,
    '--clinic-brand-primary': theme.theme.colors.primary,
    '--clinic-brand-primary-dark':
      theme.theme.colors.primaryDark || theme.theme.colors.primary,
  }

  if (atmosphere) {
    pageStyle.backgroundImage = `
      radial-gradient(ellipse 70% 45% at 0% 0%, ${hexToRgba(theme.theme.colors.primary, 0.14)}, transparent 55%),
      radial-gradient(ellipse 50% 35% at 100% 0%, ${hexToRgba(theme.theme.colors.primaryLight || theme.theme.colors.primary, 0.1)}, transparent 50%)
    `
  }

  const socialHrefs = new Set(
    (clinicContent?.socialMedia || [])
      .map((social) => resolveHref(social.link || social.url))
      .filter(Boolean)
  )

  // Keep configured footer quick links (Privacy Policy, etc.), but do not
  // surface social profiles here — those belong in Connect only.
  const quickLinks = (theme.footer.links || []).filter((link) => {
    const href = resolveHref(link.url || link.link)
    return !href || !socialHrefs.has(href)
  })

  const footerHasLinks = theme.footer.showLinks && quickLinks.length > 0
  const footerHasHours =
    theme.footer.showClinicHours && clinicContent?.hours?.length > 0
  const footerHasSocial =
    theme.footer.showSocialIcons && clinicContent?.socialMedia?.length > 0

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <div className="clinic-theme-page" style={pageStyle}>
        <header
          className={`clinic-theme-header${theme.header.sticky ? ' is-sticky' : ''}`}
          style={{
            position: theme.header.sticky ? 'sticky' : 'relative',
            top: 0,
            zIndex: 100,
            backgroundColor: headerBgColor,
            color: theme.header.textColor,
          }}
        >
          <Container maxWidth="lg" className="clinic-theme-header-inner">
            {showLogo && (
              <a
                href={theme.logo.link || '#'}
                className="clinic-theme-logo-link"
              >
                <span className="clinic-theme-logo-plate">
                  <img
                    src={theme.logo.url}
                    alt={theme.logo.alt || clinicName}
                    style={{
                      height: theme.header.logoHeight || '64px',
                      width: theme.logo.width || 'auto',
                      maxHeight: '72px',
                    }}
                  />
                </span>
              </a>
            )}

            <div
              className="clinic-theme-brand-details"
              style={{ fontSize: baseFontSize }}
            >
              {theme.header.showClinicName && (
                <h1 className="clinic-theme-clinic-name" style={{ fontSize: largeFontSize }}>
                  {clinicName}
                </h1>
              )}

              {formattedAddress && (
                <p className="clinic-theme-detail-line clinic-theme-detail-address">
                  <i className="bi bi-geo-alt-fill" aria-hidden="true" />
                  <span>{formattedAddress}</span>
                </p>
              )}

              <div className="clinic-theme-detail-contacts">
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="clinic-theme-detail-line clinic-theme-detail-link"
                  >
                    <i className="bi bi-envelope-fill" aria-hidden="true" />
                    <span>{email}</span>
                  </a>
                )}
                {phone && (
                  <a
                    href={`tel:${phone}`}
                    className="clinic-theme-detail-line clinic-theme-detail-link"
                  >
                    <i className="bi bi-telephone-fill" aria-hidden="true" />
                    <span>{phone}</span>
                  </a>
                )}
                {websiteHref && (
                  <a
                    href={websiteHref}
                    className="clinic-theme-detail-line clinic-theme-detail-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="bi bi-globe2" aria-hidden="true" />
                    <span>{website}</span>
                  </a>
                )}
              </div>
            </div>
          </Container>
        </header>

        <main className={`clinic-theme-main ${contentClassName}`.trim()}>
          <Container maxWidth={contentMaxWidth} sx={{ position: 'relative', zIndex: 1 }}>
            {typeof children === 'function'
              ? children({ theme, clinicContent, clinicName })
              : children}
          </Container>
        </main>

        <footer
          className="clinic-theme-footer"
          style={{
            backgroundColor: footerBgColor,
            color: theme.footer.textColor,
          }}
        >
          <Container maxWidth="lg">
            <div className="clinic-theme-footer-grid">
              <div className="clinic-theme-footer-brand">
                <p className="clinic-theme-footer-label">About</p>
                <h2 className="clinic-theme-footer-title">{clinicName}</h2>
                {clinicContent?.tagline && (
                  <p className="clinic-theme-footer-tagline">{clinicContent.tagline}</p>
                )}
                {theme.footer.showContactInfo && (
                  <ul className="clinic-theme-footer-list">
                    {formattedAddress && (
                      <li>
                        <i className="bi bi-geo-alt" aria-hidden="true" />
                        <span>{formattedAddress}</span>
                      </li>
                    )}
                    {phone && (
                      <li>
                        <i className="bi bi-telephone" aria-hidden="true" />
                        <a href={`tel:${phone}`}>{phone}</a>
                      </li>
                    )}
                    {email && (
                      <li>
                        <i className="bi bi-envelope" aria-hidden="true" />
                        <a href={`mailto:${email}`}>{email}</a>
                      </li>
                    )}
                  </ul>
                )}
              </div>

              {footerHasLinks && (
                <div className="clinic-theme-footer-col">
                  <p className="clinic-theme-footer-label">Quick links</p>
                  <ul className="clinic-theme-footer-links">
                    {quickLinks.map((link, index) => (
                      <li key={`${link.text}-${index}`}>
                        <a href={link.url || link.link || '#'}>{link.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(footerHasHours || footerHasSocial) && (
                <div className="clinic-theme-footer-col">
                  {footerHasHours && (
                    <>
                      <p className="clinic-theme-footer-label">Hours</p>
                      <ul className="clinic-theme-footer-hours">
                        {clinicContent.hours.map((hour, index) => (
                          <li key={index}>
                            <span className="day">{hour.day}</span>
                            <span className="time">{hour.workHours}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {footerHasSocial && (
                    <div className={`clinic-theme-social${footerHasHours ? ' has-spacing' : ''}`}>
                      <p className="clinic-theme-footer-label">Connect</p>
                      <div className="clinic-theme-social-row">
                        {clinicContent.socialMedia.map((social, index) => {
                          const href = resolveHref(social.link || social.url) || '#'
                          return (
                          <a
                            key={index}
                            href={href}
                            className="clinic-theme-social-btn"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={social.type || 'Social link'}
                          >
                            <i className={socialIconClass(social.type)} aria-hidden="true" />
                          </a>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="clinic-theme-footer-bottom">
              <Typography component="p" className="clinic-theme-copyright">
                {(theme.footer.text || '').replace('<<CLINIC_NAME>>', clinicName)}
              </Typography>
              {theme.footer.showPoweredBy && (
                <Typography component="p" className="clinic-theme-powered">
                  {theme.footer.poweredByText}
                </Typography>
              )}
            </div>
          </Container>
        </footer>
      </div>
    </ThemeProvider>
  )
}

export default ClinicThemeShell
